// The full emoji picker — port of ios/Sources/Views/EmojiPickerSheet.swift: recents first, then the
// whole webapp catalogue, then the server's custom emoji (sorted, so a live `emoji_added` never
// reshuffles the grid), searchable by shortcode, with an explicit way out (Esc, the scrim, a click
// outside — the Mac's "I cannot close this window" trap, 2026-09-03/14). Emits the NAME: the wire
// wants shortcodes; the glyph is presentation.
//
// WEB additions, because a pointer and a keyboard expect them: category tabs (the webapp's own
// categories and order), a hover/keyboard preview with the big emoji and its `:name:`, arrow keys
// over the grid + Enter, and a popover anchored where it was asked for (a bottom sheet at phone
// width). Always a PORTAL: rows use `content-visibility: auto`, which clips anything inside them.
//
// Open time: ~1,800 emoji must appear at once. The grid is virtualised by ROW — every row is a fixed
// height (a header or one line of cells), so the whole layout is arithmetic and only the rows in
// view (plus a margin) exist in the DOM. Measured on open and logged as `[satori-perf] emojiPicker`.
import { useCallback, useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { EmojiPicker as Model, type GridPosition } from '../../../core/emojiPicker';
import { Icon } from '../../../design/icons';
import { t, tf } from '../../../i18n/i18n';
import { mediaUrls } from '../../media';
import { CategoryIcon } from './categoryIcons';
import { useCustomEmojiList } from './customEmoji';
import { rememberEmoji, useRecentsRaw } from './recents';
import './emoji.css';

const CELL = 36;
const HEADER = 28;
const SIDE_PAD = 8;
const POPOVER_WIDTH = 352;
const POPOVER_HEIGHT = 420;
const PHONE_WIDTH = 600;
const OVERSCAN = 3 * CELL;

export interface AnchorRect {
  left: number;
  top: number;
  right: number;
  bottom: number;
}

export interface EmojiPickerProps {
  /** Where it was asked for (a button's bounding rect). */
  anchor: AnchorRect;
  /** The dialog's accessible name: «Reaccionar» for a reaction, «Emoji» in the composer. */
  title: string;
  /** `custom` = a server emoji (no glyph exists; it travels as `:name:`). */
  onPick(name: string, custom: boolean): void;
  onClose(): void;
  /** Open above the anchor when there is room (the composer sits at the bottom). */
  prefer?: 'above' | 'below';
  /** performance.now() of the click that opened it — the open-time measurement's start. */
  startedAt?: number;
}

interface Item {
  name: string;
  char: string | null;
  customId: string | null;
  names: string[];
}

interface Section {
  id: string;
  title: string;
  items: Item[];
  /** Search results are one untitled grid (Apple: the filtered grid, no header). */
  header: boolean;
}

interface Row {
  section: number;
  /** First item index of this line (cells rows only). */
  start: number;
  top: number;
  header: boolean;
}

let catalogItems: Array<{ id: string; items: Item[] }> | null = null;
function catalogSections(): Array<{ id: string; items: Item[] }> {
  if (!catalogItems) {
    catalogItems = Model.categories().map((c) => ({ id: c.id, items: c.emoji.map((e) => ({ name: e.name, char: e.char, customId: null, names: e.names })) }));
  }
  return catalogItems;
}

function layout(sections: Section[], columns: number): { rows: Row[]; height: number; tops: number[] } {
  const rows: Row[] = [];
  const tops: number[] = [];
  let y = 0;
  sections.forEach((s, i) => {
    tops.push(y);
    if (!s.items.length) return;
    if (s.header) {
      rows.push({ section: i, start: 0, top: y, header: true });
      y += HEADER;
    }
    for (let start = 0; start < s.items.length; start += columns) {
      rows.push({ section: i, start, top: y, header: false });
      y += CELL;
    }
  });
  return { rows, height: y, tops };
}

function useIsPhone(): boolean {
  const [phone, setPhone] = useState(() => window.innerWidth <= PHONE_WIDTH);
  useEffect(() => {
    const onResize = () => setPhone(window.innerWidth <= PHONE_WIDTH);
    window.addEventListener('resize', onResize);
    return () => window.removeEventListener('resize', onResize);
  }, []);
  return phone;
}

export function EmojiPicker(p: EmojiPickerProps) {
  const opened = useRef(p.startedAt ?? performance.now());
  const phone = useIsPhone();
  const root = useRef<HTMLDivElement>(null);
  const scroller = useRef<HTMLDivElement>(null);
  const input = useRef<HTMLInputElement>(null);
  const [query, setQuery] = useState('');
  const [scrollTop, setScrollTop] = useState(0);
  const [viewport, setViewport] = useState({ width: (phone ? window.innerWidth : POPOVER_WIDTH) - 2 * SIDE_PAD - 12, height: 300 });
  const [active, setActive] = useState<GridPosition>({ section: 0, index: 0 });
  const recentsRaw = useRecentsRaw();
  const custom = useCustomEmojiList();
  const close = useRef(p.onClose);
  close.current = p.onClose;

  const customById = useMemo(() => new Map(custom.map((c) => [c.name, c.id])), [custom]);
  const toItem = useCallback(
    (name: string): Item => {
      const customId = customById.get(name) ?? null;
      return { name, char: customId ? null : Model.glyph(name), customId, names: [name] };
    },
    [customById],
  );

  const sections: Section[] = useMemo(() => {
    const trimmed = query.trim();
    if (trimmed) {
      const items: Item[] = Model.searchAll(trimmed, [...customById.keys()]).map((hit) =>
        hit.kind === 'custom' ? toItem(hit.name) : { name: hit.emoji.name, char: hit.emoji.char, customId: null, names: hit.emoji.names },
      );
      return [{ id: 'results', title: t('web.emoji.results'), items, header: false }];
    }
    const out: Section[] = [];
    const recent = Model.recents(recentsRaw, (n) => customById.has(n));
    if (recent.length) out.push({ id: 'recents', title: t('channel.emojiRecents'), items: recent.map(toItem), header: true });
    for (const c of catalogSections()) out.push({ id: c.id, title: t(`web.emoji.category.${c.id}`), items: c.items, header: true });
    if (customById.size) out.push({ id: 'custom', title: t('web.emoji.category.custom'), items: Model.searchCustom('', [...customById.keys()]).map(toItem), header: true });
    return out;
  }, [query, recentsRaw, customById, toItem]);

  const columns = Math.max(4, Math.floor(viewport.width / CELL));
  const { rows, height, tops } = useMemo(() => layout(sections, columns), [sections, columns]);
  const sizes = useMemo(() => sections.map((s) => s.items.length), [sections]);
  const activeItem = sections[active.section]?.items[active.index] ?? null;

  // The open: focus the search (Apple: `.searchable` always visible), report the time to the first
  // painted frame, and measure the grid's real width (scrollbars differ per platform).
  useLayoutEffect(() => {
    input.current?.focus({ preventScroll: true });
    const el = scroller.current;
    if (el) setViewport({ width: el.clientWidth - 2 * SIDE_PAD, height: el.clientHeight });
    requestAnimationFrame(() => {
      const ms = performance.now() - opened.current;
      console.info(`[satori-perf] emojiPicker ${Math.round(ms)}ms ${rows.length} rows`);
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);
  useEffect(() => {
    const el = scroller.current;
    if (!el) return;
    const ro = new ResizeObserver(() => setViewport({ width: el.clientWidth - 2 * SIDE_PAD, height: el.clientHeight }));
    ro.observe(el);
    return () => ro.disconnect();
  }, []);

  // A new query starts at the top, on its first result.
  useEffect(() => {
    setActive({ section: 0, index: 0 });
    if (scroller.current) scroller.current.scrollTop = 0;
    setScrollTop(0);
  }, [query]);

  // A click anywhere else closes (the opener toggles itself: it carries data-emoji-opener).
  useEffect(() => {
    const onDown = (e: MouseEvent) => {
      const target = e.target as Element | null;
      if (root.current?.contains(target as Node)) return;
      if (target?.closest?.('[data-emoji-opener]')) return;
      close.current();
    };
    const id = setTimeout(() => window.addEventListener('mousedown', onDown));
    return () => {
      clearTimeout(id);
      window.removeEventListener('mousedown', onDown);
    };
  }, []);

  const pick = (item: Item) => {
    rememberEmoji(item.name);
    p.onPick(item.name, item.customId != null);
  };

  const reveal = (pos: GridPosition) => {
    const el = scroller.current;
    if (!el) return;
    const row = rows.find((r) => !r.header && r.section === pos.section && pos.index >= r.start && pos.index < r.start + columns);
    if (!row) return;
    const isFirstRow = row.start === 0 && sections[pos.section]?.header;
    const top = isFirstRow ? row.top - HEADER : row.top;
    if (top < el.scrollTop) el.scrollTop = top;
    else if (row.top + CELL > el.scrollTop + el.clientHeight) el.scrollTop = row.top + CELL - el.clientHeight;
  };

  const onKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Escape') {
      e.preventDefault();
      e.stopPropagation();
      p.onClose();
      return;
    }
    if (e.key === 'ArrowDown' || e.key === 'ArrowUp' || e.key === 'ArrowLeft' || e.key === 'ArrowRight') {
      e.preventDefault();
      e.stopPropagation();
      const next = Model.move(sizes, columns, active, e.key);
      setActive(next);
      reveal(next);
      return;
    }
    if (e.key === 'Enter' && !e.nativeEvent.isComposing) {
      e.preventDefault();
      e.stopPropagation();
      if (activeItem) pick(activeItem);
    }
  };

  const jumpTo = (section: number) => {
    const el = scroller.current;
    if (el) el.scrollTop = tops[section];
    setScrollTop(tops[section]);
    setActive({ section, index: 0 });
  };

  // The tab under the reader: the last section whose top is at or above the scroll position.
  // At the very bottom the last section is the one being read, even when it is too short to reach
  // the top (the custom emoji, after a jump to them).
  let currentSection = 0;
  const atBottom = scrollTop > 0 && scrollTop + viewport.height >= height - 2;
  for (let i = 0; i < tops.length; i++) if (sections[i].items.length && (tops[i] <= scrollTop + 4 || atBottom)) currentSection = i;

  const from = scrollTop - OVERSCAN;
  const to = scrollTop + viewport.height + OVERSCAN;
  const visible = rows.filter((r) => r.top + (r.header ? HEADER : CELL) >= from && r.top <= to);
  const inset = SIDE_PAD + Math.max(0, Math.floor((viewport.width - columns * CELL) / 2));

  // Placement: under (or over) the anchor, aligned to whichever edge keeps it nearest, on screen.
  const style: React.CSSProperties = {};
  if (!phone) {
    const h = Math.min(POPOVER_HEIGHT, window.innerHeight - 16);
    const w = Math.min(POPOVER_WIDTH, window.innerWidth - 16);
    const below = p.anchor.bottom + 6;
    const above = p.anchor.top - 6 - h;
    const fitsBelow = below + h <= window.innerHeight - 8;
    const fitsAbove = above >= 8;
    let top = p.prefer === 'above' ? (fitsAbove ? above : fitsBelow ? below : 8) : fitsBelow ? below : fitsAbove ? above : window.innerHeight - h - 8;
    top = Math.max(8, Math.min(top, window.innerHeight - h - 8));
    const centre = (p.anchor.left + p.anchor.right) / 2;
    let left = centre > window.innerWidth / 2 ? p.anchor.right - w : p.anchor.left;
    left = Math.max(8, Math.min(left, window.innerWidth - w - 8));
    Object.assign(style, { left, top, width: w, height: h });
  }

  const picker = (
    <div
      ref={root}
      className={`emoji-picker${phone ? ' sheet' : ' popover-like'}`}
      role="dialog"
      aria-label={p.title}
      style={style}
      onKeyDown={onKeyDown}
      onContextMenu={(e) => e.stopPropagation()}
    >
      <div className="ep-top">
        <label className="ep-search">
          <Icon name="search" size={15} />
          <input
            ref={input}
            type="search"
            spellCheck={false}
            autoComplete="off"
            value={query}
            placeholder={t('web.emoji.search')}
            aria-label={t('web.emoji.search')}
            onChange={(e) => setQuery(e.target.value)}
          />
        </label>
        <button type="button" className="icon-button ep-close" onClick={() => p.onClose()} aria-label={t('common.close')} title={t('common.close')}>
          <Icon name="x" size={16} />
        </button>
      </div>
      {!query.trim() ? (
        <div className="ep-tabs" role="tablist">
          {sections.map((s, i) => (
            <button
              key={s.id}
              type="button"
              role="tab"
              aria-selected={i === currentSection}
              title={s.title}
              aria-label={s.title}
              tabIndex={-1}
              onMouseDown={(e) => e.preventDefault()}
              onClick={() => jumpTo(i)}
            >
              <CategoryIcon id={s.id} />
            </button>
          ))}
        </div>
      ) : null}
      <div
        className="ep-scroll"
        ref={scroller}
        onScroll={(e) => setScrollTop((e.target as HTMLDivElement).scrollTop)}
        role="grid"
        aria-label={p.title}
      >
        <div className="ep-canvas" style={{ height }}>
          {visible.map((r) => {
            const s = sections[r.section];
            if (r.header) {
              return (
                <div key={`h-${s.id}`} className="ep-header" style={{ top: r.top, left: inset }}>
                  {s.title}
                </div>
              );
            }
            return (
              <div key={`r-${s.id}-${r.start}`} className="ep-row" role="row" style={{ top: r.top, left: inset }}>
                {s.items.slice(r.start, r.start + columns).map((item, k) => {
                  const index = r.start + k;
                  const on = active.section === r.section && active.index === index;
                  return (
                    <button
                      key={item.name}
                      type="button"
                      role="gridcell"
                      className={`ep-cell${on ? ' active' : ''}`}
                      aria-label={`:${item.name}:`}
                      aria-selected={on}
                      tabIndex={-1}
                      onMouseDown={(e) => e.preventDefault()}
                      onMouseEnter={() => setActive({ section: r.section, index })}
                      onClick={() => pick(item)}
                    >
                      {item.customId ? <img src={mediaUrls.customEmoji(item.customId)} alt="" loading="lazy" decoding="async" draggable={false} /> : item.char ?? `:${item.name}:`}
                    </button>
                  );
                })}
              </div>
            );
          })}
        </div>
        {!rows.length ? <div className="ep-empty">{tf('web.emoji.noResults', query.trim())}</div> : null}
      </div>
      <div className="ep-preview" aria-live="polite">
        {activeItem ? (
          <>
            <span className="ep-preview-glyph">
              {activeItem.customId ? <img src={mediaUrls.customEmoji(activeItem.customId)} alt="" /> : activeItem.char ?? ''}
            </span>
            <span className="ep-preview-names">
              <span className="ep-preview-name">:{activeItem.name}:</span>
              {activeItem.names.length > 1 ? <span className="ep-preview-alias">{activeItem.names.slice(1).map((n) => `:${n}:`).join(' ')}</span> : null}
            </span>
          </>
        ) : (
          <span className="ep-preview-hint">{t('web.emoji.hint')}</span>
        )}
      </div>
    </div>
  );

  if (phone) {
    return createPortal(
      <div className="ep-scrim" onMouseDown={(e) => e.target === e.currentTarget && p.onClose()}>
        {picker}
      </div>,
      document.body,
    );
  }
  return createPortal(picker, document.body);
}
